import { ServiceAuth } from '../../hooks/useService/useService';
import FhirSearchService from '../../services/FhirSearchService';
import {
  ExtractedClinicalResource,
  getCollectionDate,
  getExtractedResourceValue,
} from './useMemberExtractionDocuments';

export const VALUE_HISTORY_PAGE_SIZE = 5;

const fhirSearchService = new FhirSearchService();

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export type MemberExtractionHistoryEntry = {
  id: string;
  collectedAt?: string;
  value?: string;
};

type SearchResponse<T> = {
  hits?: { hits?: Array<{ _source?: T }> };
};

export const formatCollectionDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  if (!match || !month) {
    return value;
  }
  return `${month} ${Number(match[3])}, ${match[1]}`;
};

// One past the visible page detects whether more exist, and one more makes
// room for the result being viewed, which is dropped from its own history.
export const valueHistoryFetchLimit = (visibleCount: number) =>
  visibleCount + 2;

export const toHistoryEntries = (
  resources: ExtractedClinicalResource[],
  excludeId?: string,
): MemberExtractionHistoryEntry[] =>
  resources
    .flatMap(resource => {
      if (!resource.id || resource.id === excludeId) {
        return [];
      }
      const collectedAt = getCollectionDate(resource);
      return [
        {
          id: resource.id,
          collectedAt,
          value: getExtractedResourceValue(resource),
        },
      ];
    })
    .sort((left, right) =>
      (right.collectedAt ?? '').localeCompare(left.collectedAt ?? ''),
    );

export const buildMemberExtractionValueHistoryQuery = (
  system: string,
  code: string,
  limit: number,
) => ({
  type: 'select',
  columns: '*',
  from: [{ table: 'observation' }],
  orderby: [
    {
      type: 'DESC',
      expr: {
        type: 'column_ref',
        column: 'effectiveDateTime',
      },
    },
  ],
  limit: [
    { type: 'number', value: 0 },
    { type: 'number', value: limit },
  ],
  where: {
    type: 'elasticsearch',
    query: {
      bool: {
        must: [
          { term: { 'code.coding.system.keyword': system } },
          { term: { 'code.coding.code.keyword': code } },
        ],
      },
    },
  },
});

export const loadMemberExtractionValueHistory = async (
  auth: ServiceAuth,
  projectId: string,
  patientId: string,
  system: string,
  code: string,
  limit: number,
) => {
  const response = await fhirSearchService.searchPatient<
    SearchResponse<ExtractedClinicalResource>
  >(auth)(
    projectId,
    patientId,
    buildMemberExtractionValueHistoryQuery(system, code, limit),
    undefined,
    { queryId: 'member-extraction-value-history' },
  );
  return (response?.data?.hits?.hits ?? [])
    .map(hit => hit._source)
    .filter((source): source is ExtractedClinicalResource => source != null);
};

const HISTORY_CACHE_TTL_MS = 60_000;

type HistoryCacheEntry = {
  promise: Promise<ExtractedClinicalResource[]>;
  resources?: ExtractedClinicalResource[];
  expiresAt: number;
};

const historyCache = new Map<string, HistoryCacheEntry>();

const historyCacheKey = (
  projectId: string,
  patientId: string,
  system: string,
  code: string,
  limit: number,
) => JSON.stringify([projectId, patientId, system, code, limit]);

const freshCacheEntry = (key: string) => {
  const entry = historyCache.get(key);
  if (entry && entry.expiresAt <= Date.now()) {
    historyCache.delete(key);
    return undefined;
  }
  return entry;
};

export const peekMemberExtractionValueHistory = (
  projectId: string,
  patientId: string,
  system: string,
  code: string,
  limit: number,
) =>
  freshCacheEntry(historyCacheKey(projectId, patientId, system, code, limit))
    ?.resources;

export const requestMemberExtractionValueHistory = (
  auth: ServiceAuth,
  projectId: string,
  patientId: string,
  system: string,
  code: string,
  limit: number,
) => {
  const key = historyCacheKey(projectId, patientId, system, code, limit);
  const cached = freshCacheEntry(key);
  if (cached) {
    return cached.promise;
  }
  const entry: HistoryCacheEntry = {
    promise: loadMemberExtractionValueHistory(
      auth,
      projectId,
      patientId,
      system,
      code,
      limit,
    ).then(
      resources => {
        entry.resources = resources;
        return resources;
      },
      error => {
        historyCache.delete(key);
        throw error;
      },
    ),
    expiresAt: Date.now() + HISTORY_CACHE_TTL_MS,
  };
  historyCache.set(key, entry);
  return entry.promise;
};

export const clearMemberExtractionValueHistoryCache = () =>
  historyCache.clear();
